import type { LetterMasters } from "@/types/letterform"
import { mapClamped } from "@/utils/math"

// Builds a parallel array mapping each coordinate index to its axis.
function buildAxisMap(commands: LetterMasters["commands"]): ("x" | "y")[] {
  const axes: ("x" | "y")[] = []
  for (const cmd of commands) {
    if (cmd === "M") {
      axes.push("x", "y")
    } else if (cmd === "H") {
      axes.push("x")
    } else if (cmd === "V") {
      axes.push("y")
    }
  }
  return axes
}

// The interpolated, screen-space result for a letterform at a given size.
// `coords` aligns 1:1 with the non-Z entries of `commands`; `axes` labels each.
export type LetterformCoords = {
  coords: number[]
  axes: ("x" | "y")[]
  commands: LetterMasters["commands"]
}

// Pure function — no reactivity here, just math.
// Interpolates the two masters at the container's aspect ratio and returns the
// final screen-space coordinate array (after gap/link enforcement) plus its
// axis map. Callers that just want a path string can use `interpolatePath`.
export function computeLetterformCoords(
  masters: LetterMasters,
  width: number,
  height: number,
  minGapScale = 1,
): LetterformCoords {
  const axes = buildAxisMap(masters.commands)

  if (width === 0 || height === 0) {
    return { coords: [], axes, commands: masters.commands }
  }

  const aspect = width / height
  const t = mapClamped(aspect, masters.minAspect, masters.maxAspect, 0, 1)

  const [narrowW, narrowH] = masters.narrowArtboard
  const [wideW, wideH] = masters.wideArtboard

  const artW = narrowW + t * (wideW - narrowW)
  const artH = narrowH + t * (wideH - narrowH)

  const scaleX = width / artW
  const scaleY = height / artH

  // Interpolate in artboard space, then scale directly to screen space.
  const coords = masters.narrowTall.map((v, i) => {
    const art = v + t * (masters.wideShort[i] - v)
    return art * (axes[i] === "x" ? scaleX : scaleY)
  })

  // Enforce minimum screen-pixel gaps between declared coordinate pairs.
  for (const gap of masters.minGaps ?? []) {
    const a = coords[gap.indexA]
    const b = coords[gap.indexB]
    const dist = Math.abs(a - b)
    const scaledMin = gap.minPx * minGapScale
    const scaledTarget =
      gap.targetPx !== undefined ? gap.targetPx * minGapScale : undefined
    const enforcedPx =
      scaledTarget !== undefined ? Math.max(scaledTarget, scaledMin) : scaledMin
    if (Math.abs(dist - enforcedPx) > 0.001) {
      const mid = (a + b) / 2
      const half = enforcedPx / 2
      coords[gap.indexA] = a <= b ? mid - half : mid + half
      coords[gap.indexB] = a <= b ? mid + half : mid - half
    }
  }

  for (const link of masters.linkedPairs ?? []) {
    coords[link.target] = coords[link.source]
  }

  return { coords, axes, commands: masters.commands }
}

// Walks the command sequence and emits an SVG path `d` string from a
// screen-space coordinate array (as produced by `computeLetterformCoords`).
export function buildPathD(
  coords: number[],
  commands: LetterMasters["commands"],
): string {
  if (coords.length === 0) return ""

  let ci = 0
  let d = ""
  for (const cmd of commands) {
    if (cmd === "M") {
      d += `M${coords[ci++]} ${coords[ci++]}`
    } else if (cmd === "H") {
      d += `H${coords[ci++]}`
    } else if (cmd === "V") {
      d += `V${coords[ci++]}`
    } else if (cmd === "Z") {
      d += "Z"
    }
  }
  return d
}

// Convenience wrapper: interpolate and build the path string in one call.
// Takes a LetterMasters definition and the current container dimensions,
// returns a ready-to-use SVG path `d` string.
export function interpolatePath(
  masters: LetterMasters,
  width: number,
  height: number,
  minGapScale = 1,
): string {
  const { coords, commands } = computeLetterformCoords(
    masters,
    width,
    height,
    minGapScale,
  )
  return buildPathD(coords, commands)
}
